/**
 * The bridge between tour mode and the api client.
 *
 * While a tour runs, every request the application makes is answered from the
 * sample layer instead of the network. The swap is done by handing axios a
 * different adapter for the request, so no connection is opened at all — this
 * is a local data source, not a stub server.
 *
 * Two kinds of request are deliberately left alone, because they are session
 * infrastructure rather than tour data: blocking them would sign the user out
 * mid-tour, and neither one puts tenant records on screen.
 */

import { AxiosRequestConfig } from 'axios';

import { isTourMode } from '../tour-mode';

import { tourAdapter } from './tour-data-source';
import { registerAllTourRoutes } from './tour-routes';

/**
 * Requests that keep the session alive and decide what the user may see.
 * They carry no tour data — the tour's screens are all fed from fixtures — so
 * they continue to reach the backend (see the Known Limitations note in the
 * Help & Tours README).
 */
const SESSION_PATHS = [
  '/auth/',
  '/AdminManagement/admin/profile',
  '/AwardManagement/master/user-permissions',
];

const isSessionRequest = (url: string | undefined): boolean =>
  !!url && SESSION_PATHS.some(path => url.includes(path));

/**
 * True when this request should be answered from sample data. Exported for the
 * few places outside axios that open their own connection (the report SSE
 * stream) and need to make the same decision.
 */
export const shouldUseTourData = (url?: string): boolean =>
  isTourMode() && !isSessionRequest(url);

/**
 * Point a request at the sample-data adapter when a tour is running. Returns
 * the config unchanged otherwise, so normal application traffic is untouched.
 */
export const applyTourAdapter = <T extends AxiosRequestConfig>(
  config: T
): T => {
  if (!shouldUseTourData(config.url)) return config;
  // Idempotent, and cheap after the first call.
  registerAllTourRoutes();
  config.adapter = tourAdapter as T['adapter'];
  return config;
};
